import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { BACKGROUND_OPTIONS } from '../../design-system/backgrounds';
import { ACCENTS, useAppearance } from '../../design-system/providers/appearance';
import styles from './AppearanceSettings.module.css';

/**
 * Accent colour and background image, on the Settings screen — the same two
 * choices the topbar's palette and picture menus offer, for a reader who
 * looks for them where every other preference lives.
 *
 * Applied the moment they are picked, with no Save: like the topbar menus,
 * they are this browser's preference (stored beside the theme), not part of
 * the account form above whose Save writes to the server.
 *
 * Real radio groups rather than buttons: one value out of a set, arrow keys
 * move between them, and a screen reader announces "3 of 6, checked". The
 * radio itself is visually hidden; the swatch or thumbnail is its face.
 */
export function AppearanceSettings() {
  const { t } = useTranslation();
  const { accent, background, setAccent, setBackground } = useAppearance();
  const id = useId();

  return (
    <div className={styles['groups']}>
      <fieldset className={styles['group']}>
        <legend className={styles['legend']}>{t('a11y.accentSwitch')}</legend>
        <div className={styles['swatches']}>
          {ACCENTS.map((value) => (
            <label key={value} className={styles['swatchOption']}>
              <input
                type="radio"
                name={`${id}accent`}
                value={value}
                checked={accent === value}
                className={styles['radio']}
                onChange={() => {
                  setAccent(value);
                }}
              />
              {/* `data-accent` makes the token sheet repaint --color-accent for this swatch. */}
              <span className={styles['swatch']} data-accent={value} aria-hidden="true" />
              <span className={styles['optionLabel']}>{t(`accent.${value}`)}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <fieldset className={styles['group']}>
        <legend className={styles['legend']}>{t('a11y.backgroundSwitch')}</legend>
        <div className={styles['thumbs']}>
          {BACKGROUND_OPTIONS.map((option) => (
            <label key={option.id} className={styles['thumbOption']}>
              <input
                type="radio"
                name={`${id}background`}
                value={option.id}
                checked={background === option.id}
                className={styles['radio']}
                onChange={() => {
                  setBackground(option.id);
                }}
              />
              <span className={styles['thumb']} aria-hidden="true">
                {option.url === null ? null : <img src={option.url} alt="" loading="lazy" />}
              </span>
              <span className={styles['optionLabel']}>{t(option.labelKey)}</span>
            </label>
          ))}
        </div>
      </fieldset>
    </div>
  );
}
